import { forwardRef } from "react";

import * as stylex from "@stylexjs/stylex";

import { colors } from "../../styles/tokens.stylex";
import ErrorMessage from "../error-message";

export type CheckboxProps = React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
> & {
    label?: string;
    errorMessage?: string;
    id: string;
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ id, label, children, checked, errorMessage, ...rest }, ref) => {
        return (
            <div {...stylex.props(styles.checkbox, errorMessage ? styles.error : null)}>
                <input
                    id={id}
                    {...rest}
                    {...stylex.props(
                        styles.input,
                        errorMessage ? styles.onError : null,
                        checked ? styles.checked : null,
                    )}
                    type="checkbox"
                    checked={checked}
                    ref={ref}
                />
                <label htmlFor={id} {...stylex.props(styles.label)}>
                    {label || children}
                </label>
                <ErrorMessage styleX={styles.errorText} errorMessage={errorMessage} />
            </div>
        );
    },
);

export default Checkbox;

const styles = stylex.create({
    checkbox: {
        display: "grid",
        // eslint-disable-next-line quotes
        gridTemplateAreas: '"input text"',
        gridTemplateColumns: "1.25em auto",
        gap: "0 2em",
        alignItems: "center",
        fontSize: "0.6875em",
    },
    input: {
        gridArea: "input",
        appearance: "none",
        backgroundColor: colors.bg_Primary,
        margin: 0,
        cursor: {
            default: "pointer",
            ":disabled": "not-allowed",
        },

        fontSize: "1.6em",
        color: colors.fg_Disabled,
        width: "1.25em",
        height: "1.25em",
        borderWidth: "0.0625em",
        borderStyle: "solid",
        borderColor: {
            default: colors.fg_Disabled,
            ":focus": colors.b_Primary,
        },
        borderRadius: "0.25em",
        transform: "translateY(-0.075em)",
        display: "grid",
        placeContent: "center",

        "::before": {
            content: "",
            width: "1em",
            height: "1em",
            clipPath: "polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%)",
            transform: "scale(0)",
            transformOrigin: "bottom left",
            transition: "120ms transform ease-in-out",
            boxShadow: `inset 1em 1em ${colors.fg_BrandMagenta}`,
        },
    },

    checked: {
        "::before": {
            transform: "scale(1)",
        },
    },

    error: {
        borderColor: colors.b_Error,
        // eslint-disable-next-line quotes
        gridTemplateAreas: '"input text" "empty error"',
        boxShadow: "none",
    },

    onError: {
        borderColor: {
            default: colors.b_Error,
            ":focus": colors.b_Error,
        },
    },

    errorText: {
        gridArea: "error",
        color: colors.b_Error,
    },

    label: {
        gridArea: "text",
        margin: 0,
        fontWeight: "normal",
    },
});
